import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  ApiResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';

import { Offer, OfferStatus } from '../../../models/offer.model';
import { OfferService } from '../../../services/offer.service';
import { ProductService } from '../../../services/product.service';

import { MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { RouterLink } from '@angular/router';

import { Subject, debounceTime, takeUntil } from 'rxjs';
import {
  DropdownOption,
  SelectOption,
} from '../../../../shared/models/dropdowm.model';
import { CurrencyPipe } from '@angular/common';
import { TagStatusComponent } from '../../../../shared/components/tag-status/tag-status.component';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-offer-list',
  standalone: true,
  templateUrl: './offer-list.component.html',
  imports: [
    FormsModule,
    InputTextModule,
    SelectModule,
    TableModule,
    BreadcrumbModule,
    ToolbarModule,
    ButtonModule,
    RouterLink,
    ProgressSpinnerModule,
    ToastModule,
    CurrencyPipe,
    TagStatusComponent,
  ],
})
export class OfferListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private offerService = inject(OfferService);
  private productService = inject(ProductService);
  private messageService = inject(MessageService);
  paginationService = inject(PaginationService);

  offers = signal<Offer[]>([]);

  pageNumber = signal(1);
  pageSize = signal(10);
  totalRecords = signal(0);
  totalPages = signal(0);

  loading = signal(false);

  nameFilter?: string;
  productIdFilter?: string;
  statusFilter: OfferStatus | null = null;

  productSelectLoading = signal(false);
  productsDropdown = signal<DropdownOption[]>([]);

  statusOptions = this.offerService.getOfferStatusOptions();
  statusFilterOptions = [
    { label: 'Todos', value: null },
    { label: 'Rascunho', value: OfferStatus.Draft },
    { label: 'Ativo', value: OfferStatus.Active },
    { label: 'Inativo', value: OfferStatus.Inactive },
  ];

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  ngOnInit(): void {
    this.loadProductsDropdown();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize()
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.offerService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.nameFilter,
        this.productIdFilter,
        this.statusFilter
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.offers.set(page.items as Offer[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar ofertas',
            });
          }
        },
      });
  }

  getStatusDescription(status: OfferStatus): string {
    const option = this.statusOptions.filter(
      (x: SelectOption) => x.value == OfferStatus[status]
    );
    return option.length > 0 ? option[0].label : '-';
  }

  private loadProductsDropdown(): void {
    this.productSelectLoading.set(true);

    this.productService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.productSelectLoading.set(false);
        const options = response.data as DropdownOption[];
        this.productsDropdown.set(options);
      },
      error: () => {
        this.productSelectLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar produtos',
        });
      },
    });
  }
}
